"use client";

import Script from "next/script";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { detectIOSWebview } from "@/lib/utils/tracking";

declare global {
	interface Window {
		fbq?: (...args: unknown[]) => void;
		_fbq?: unknown;
	}
}

export default function ConditionalMetaPixel() {
	const [isTrackingAuthorized, setIsTrackingAuthorized] = useState<
		boolean | null
	>(null);
	const [isClient, setIsClient] = useState(false);
	const pathname = usePathname();

	const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;

	useEffect(() => {
		setIsClient(true);

		const initializeMetaPixel = () => {
			const isInIOSWebview = detectIOSWebview();

			if (isInIOSWebview) {
				setIsTrackingAuthorized(window.appTrackingAuthorized ?? false);
			} else {
				setIsTrackingAuthorized(true);
			}
		};

		const handleTrackingChange = () => {
			initializeMetaPixel();
		};

		initializeMetaPixel();

		window.addEventListener(
			"trackingAuthorizationChanged",
			handleTrackingChange,
		);

		return () => {
			window.removeEventListener(
				"trackingAuthorizationChanged",
				handleTrackingChange,
			);
		};
	}, []);

	useEffect(() => {
		if (!isTrackingAuthorized || typeof window.fbq !== "function") return;
		window.fbq("track", "PageView");
	}, [pathname, isTrackingAuthorized]);

	if (
		!isClient ||
		isTrackingAuthorized === null ||
		!isTrackingAuthorized ||
		!META_PIXEL_ID
	) {
		return null;
	}

	return (
		<Script
			id="meta-pixel"
			strategy="afterInteractive"
			dangerouslySetInnerHTML={{
				__html: `
					!function(f,b,e,v,n,t,s)
					{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					n.queue=[];t=b.createElement(e);t.async=!0;
					t.src=v;s=b.getElementsByTagName(e)[0];
					s.parentNode.insertBefore(t,s)}(window, document,'script',
					'https://connect.facebook.net/en_US/fbevents.js');
					fbq('init', '${META_PIXEL_ID}');
					fbq('track', 'PageView');
				`,
			}}
		/>
	);
}

export function MetaPixelNoscript() {
	const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;

	if (!META_PIXEL_ID) return null;

	return (
		<noscript>
			<img
				height="1"
				width="1"
				style={{ display: "none" }}
				src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
				alt=""
			/>
		</noscript>
	);
}
